import type {
  ActivityEvent,
  AttentionItem,
  IncidentDetail,
  OperatorTask,
  OperationsAssistantBriefing,
  PlatformPulse,
  PlatformService,
  RuntimeProbe,
  RuntimeProbes,
  ServiceDetail,
  TenantDetail,
  TenantHealthRow,
} from "@/types";
import type {
  ActivityEventApi,
  AttentionIncidentApi,
  DoctorRunApi,
  IncidentDetailApi,
  OperationsAssistantBriefingApi,
  OperatorTaskApi,
  PlatformOperationsApi,
  PlatformPulseApi,
  RuntimeProbeApi,
  RuntimeProbesApi,
  ServiceDetailApi,
  ServiceOverviewApi,
  TenantHealthApi,
} from "@/lib/api";
import { mapServiceStatus } from "@/lib/trust";

export function mapPulse(api: PlatformPulseApi): PlatformPulse {
  const storage = api.storage_health;
  return {
    status: api.status,
    activeTenants: api.active_tenants,
    totalTenants: api.total_tenants,
    runtimeDoctorStatus: api.runtime_doctor_status,
    runtimeDoctorVerdict: api.runtime_doctor_verdict,
    doctorSummary: {
      pass: api.doctor_summary.pass_count,
      warn: api.doctor_summary.warn_count,
      fail: api.doctor_summary.fail_count,
    },
    deploymentsRunning: api.deployments_running,
    lastRuntimeDoctor: api.last_runtime_doctor ?? api.last_checked,
    lastBackup: api.last_backup ?? "",
    lastChecked: api.last_checked,
    tenantsDoctorScanned: api.tenants_doctor_scanned,
    storageHealth: storage
      ? {
          band: storage.band,
          filesystemPath: storage.filesystem_path,
          filesystemUsedPercent: storage.filesystem_used_percent ?? null,
          filesystemFreeBytes: storage.filesystem_free_bytes ?? null,
          dockerImagesBytes: storage.docker_images_bytes ?? null,
          dockerBuildCacheBytes: storage.docker_build_cache_bytes ?? null,
          dockerVolumesBytes: storage.docker_volumes_bytes ?? null,
          dockerLogsBytes: storage.docker_logs_bytes ?? null,
          journalBytes: storage.journal_bytes ?? null,
          recommendedCleanup: storage.recommended_cleanup ?? [],
          largestConsumers: (storage.largest_consumers ?? []).map((c) => ({
            name: c.name,
            bytes: c.bytes,
            detail: c.detail ?? null,
          })),
          lastCleanupOk: storage.last_cleanup?.ok ?? null,
          lastCleanupReclaimedBytes: storage.last_cleanup?.space_reclaimed_bytes ?? null,
          collectedAt: storage.collected_at,
          logRotationOk: storage.log_rotation_ok ?? null,
          deployBlocked: Boolean(storage.deploy_blocked),
          findings: (storage.findings ?? []).map((f) => ({
            id: f.id,
            severity: f.severity,
            problem: f.problem,
            recommendation: f.recommendation ?? null,
          })),
          history: (storage.history ?? []).map((h) => ({
            collectedAt: h.collected_at ?? null,
            band: h.band ?? null,
            filesystemUsedPercent: h.filesystem_used_percent ?? null,
            filesystemFreeBytes: h.filesystem_free_bytes ?? null,
          })),
        }
      : null,
    live: true,
  };
}

export function mapAttention(item: AttentionIncidentApi): AttentionItem {
  return {
    id: item.id,
    severity: item.severity,
    tenantSlug: item.tenant_slug,
    tenantName: item.tenant_name,
    checkId: item.check_id,
    problem: item.problem,
    recommendedAction: item.recommended_action,
    occurredAt: item.occurred_at,
  };
}

function mapRuntimeProbe(probe: RuntimeProbeApi | null | undefined): RuntimeProbe | null {
  if (!probe) return null;
  return {
    status: probe.status,
    message: probe.message,
    checkId: probe.check_id,
    latencyMs: probe.latency_ms ?? null,
    recommendation: probe.recommendation ?? null,
    verifiedAt: probe.verified_at ?? null,
  };
}

export function mapRuntimeProbes(
  probes: RuntimeProbesApi | null | undefined,
): RuntimeProbes | null {
  if (!probes) return null;
  return {
    controlPlaneBackend: mapRuntimeProbe(probes.control_plane_backend),
    operationsApi: mapRuntimeProbe(probes.operations_api),
    authentication: mapRuntimeProbe(probes.authentication),
    internalPlatformServices: mapRuntimeProbe(probes.internal_platform_services),
    emailProcessing: mapRuntimeProbe(probes.email_processing),
    worker: mapRuntimeProbe(probes.worker),
    database: mapRuntimeProbe(probes.database),
    disk: mapRuntimeProbe(probes.disk),
    memory: mapRuntimeProbe(probes.memory),
    docker: mapRuntimeProbe(probes.docker),
    cpu: mapRuntimeProbe(probes.cpu),
  };
}

export function mapTenant(row: TenantHealthApi): TenantHealthRow {
  return {
    slug: row.slug,
    displayName: row.display_name,
    status: row.health,
    doctorStatus: row.doctor_status,
    platformVersion: row.platform_version,
    aiVersion: row.ai_version,
    lastBackup: row.last_backup,
    lastBackupBytes: row.last_backup_bytes ?? null,
    lastBackupPath: row.last_backup_path ?? null,
    lastBackupMtime: row.last_backup_mtime ?? null,
    lastBackupChecksum: row.last_backup_checksum ?? null,
    openAlerts: row.open_alerts,
    stackPort: row.stack_port,
    hasStack: row.has_stack,
    runtimeProbes: mapRuntimeProbes(row.runtime_probes),
    verifications: row.verifications ?? [],
  };
}

export function mapOperatorTask(task: OperatorTaskApi): OperatorTask {
  return {
    id: task.id,
    severity: task.severity,
    title: task.title,
    explanation: task.explanation,
    actionLabel: task.action_label,
    action: task.action ?? undefined,
    incidentId: task.incident_id,
  };
}

export function mapService(service: ServiceOverviewApi): PlatformService {
  const hasLiveData = service.has_live_data;
  return {
    id: service.id,
    name: service.name,
    status: mapServiceStatus(service.status, hasLiveData),
    lastChecked: service.last_checked,
    description: hasLiveData ? service.description : "Not available",
    affectedTenants: service.affected_tenants,
    hasLiveData,
    actionLabel: "Details",
  };
}

export function mapServiceDetail(detail: ServiceDetailApi): ServiceDetail {
  return {
    hasLiveData: detail.has_live_data,
    statusLabel: detail.has_live_data ? detail.status_label : "Not available",
    lastChecks: detail.last_checks.map((c) => ({
      label: c.label,
      result: c.result,
      message: c.message,
      at: c.at,
    })),
    affectedTenants: detail.affected_tenants,
    recentEvents: detail.recent_events,
    suggestedActions: detail.suggested_actions,
  };
}

export function mapActivity(event: ActivityEventApi): ActivityEvent {
  return {
    id: event.id,
    category: event.category,
    title: event.title,
    detail: event.detail,
    tenantSlug: event.tenant_slug ?? undefined,
    timestamp: event.timestamp,
  };
}

export function mapOperations(api: PlatformOperationsApi) {
  return {
    pulse: mapPulse(api.pulse),
    assistant: mapAssistant(api.assistant),
    attention: api.attention.map(mapAttention),
    tenants: api.tenants.map(mapTenant),
    operatorTasks: api.operator_tasks.map(mapOperatorTask),
    services: api.services.map(mapService),
    serviceDetails: Object.fromEntries(
      Object.entries(api.service_details).map(([k, v]) => [k, mapServiceDetail(v)]),
    ),
    activity: api.activity.map(mapActivity),
    platformVerifications: api.platform_verifications ?? [],
    hostVerifications: api.host_verifications ?? [],
  };
}

export function tenantToDetail(row: TenantHealthRow): TenantDetail {
  return {
    slug: row.slug,
    displayName: row.displayName,
    health: row.status,
    doctorStatus: row.doctorStatus,
    platformVersion: row.platformVersion,
    aiVersion: row.aiVersion,
    lastBackup: row.lastBackup,
    lastBackupBytes: row.lastBackupBytes,
    lastBackupPath: row.lastBackupPath,
    lastBackupMtime: row.lastBackupMtime,
    lastBackupChecksum: row.lastBackupChecksum,
    openAlerts: row.openAlerts,
    stackPort: row.stackPort ?? undefined,
    hasLiveData: row.hasStack,
    recentEvents: [],
    suggestedActions: [],
    runtimeProbes: row.runtimeProbes ?? null,
    verifications: row.verifications ?? [],
  };
}

export type DoctorRunResult = {
  preview: boolean;
  verdict: string;
  summary: { pass: number; warn: number; fail: number };
  lastRuntimeDoctor: string | null;
  tenantsScanned: number;
};

export function mapAssistant(api: OperationsAssistantBriefingApi): OperationsAssistantBriefing {
  return {
    greetingPeriod: api.greeting_period,
    platformStatus: api.platform_status,
    headline: api.headline,
    detailLines: api.detail_lines,
    customerImpact: api.customer_impact,
    primaryIncidentId: api.primary_incident_id,
    reviewButtonLabel: api.review_button_label,
    estimatedWorkMinutes: api.estimated_work_minutes,
    aiConfidence: api.ai_confidence,
    causeSummary: api.cause_summary,
    tenantName: api.tenant_name,
    recommendedMitigation: api.recommended_mitigation,
    expectedRecoverySeconds: api.expected_recovery_seconds,
  };
}

export function mapIncidentDetail(api: IncidentDetailApi): IncidentDetail {
  return {
    id: api.id,
    title: api.title,
    severity: api.severity,
    status: api.status,
    lifecyclePhase: api.lifecycle_phase,
    lifecycleLabel: api.lifecycle_label,
    tenantSlug: api.tenant_slug,
    tenantName: api.tenant_name,
    category: api.category,
    detectedAt: api.detected_at,
    lastVerifiedAt: api.last_verified_at,
    explanation: api.explanation,
    impact: {
      customerImpact: api.impact.customer_impact,
      businessImpact: api.impact.business_impact,
      platformImpact: api.impact.platform_impact,
      estimatedUrgency: api.impact.estimated_urgency,
    },
    evidenceSummary: api.evidence_summary,
    evidence: api.evidence.map((e) => ({
      kind: e.kind,
      category: e.category,
      label: e.label,
      value: e.value,
      observedAt: e.observed_at,
    })),
    recommendedAction: api.recommended_action,
    assistantBriefing: api.assistant_briefing || api.ai_reasoning,
    aiExplanation: api.ai_explanation,
    aiReasoning: api.ai_reasoning,
    aiConfidence: api.ai_confidence,
    confidenceReasons: api.confidence_reasons,
    mitigation: api.mitigation
      ? {
          primaryActionId: api.mitigation.primary_action_id,
          primaryActionLabel: api.mitigation.primary_action_label,
          alternativeActions: api.mitigation.alternative_actions,
          expectedRecoverySeconds: api.mitigation.expected_recovery_seconds,
          estimatedRisk: api.mitigation.estimated_risk,
          confidence: api.mitigation.confidence,
          confidenceReasons: api.mitigation.confidence_reasons,
          summary: api.mitigation.summary,
          what: api.mitigation.what,
          why: api.mitigation.why,
          expectedOutcome: api.mitigation.expected_outcome,
        }
      : null,
    actions: api.actions,
    logsCommand: api.logs_command,
    rawCheckId: api.raw_check_id,
    resolved: api.resolved,
    policy: api.policy
      ? {
          policy: api.policy.policy,
          status: api.policy.status,
          severity: api.policy.severity,
          confidence: api.policy.confidence,
          reason: api.policy.reason,
          recommendation: api.policy.recommendation,
          evaluatedAt: api.policy.evaluated_at,
        }
      : null,
    timeline: (api.timeline ?? []).map((event) => ({
      timestamp: event.timestamp,
      component: event.component,
      eventType: event.event_type,
      status: event.status,
      label: event.label,
      detail: event.detail,
      verificationRef: event.verification_ref,
      policyRef: event.policy_ref,
      incidentId: event.incident_id,
      deploymentId: event.deployment_id,
    })),
    rootCause: api.root_cause
      ? {
          summary: api.root_cause.summary,
          method: api.root_cause.method,
          confidence: api.root_cause.confidence,
          confidenceLabel: api.root_cause.confidence_label,
          supportingEvidence: api.root_cause.supporting_evidence,
          excludedCauses: api.root_cause.excluded_causes,
          inputs: api.root_cause.inputs ?? {},
        }
      : null,
    operationalImpact: api.operational_impact
      ? {
          affectedCapabilities: api.operational_impact.affected_capabilities,
          affectedTenants: api.operational_impact.affected_tenants,
          estimatedSeverity: api.operational_impact.estimated_severity,
          businessImpact: api.operational_impact.business_impact,
        }
      : null,
    recommendations: (api.recommendations ?? []).map((rec) => ({
      action: rec.action,
      reason: rec.reason,
      confidence: rec.confidence,
      confidenceLabel: rec.confidence_label,
      expectedOutcome: rec.expected_outcome,
      supportingEvidence: rec.supporting_evidence,
      actionId: rec.action_id,
      automationLevel: rec.automation_level ?? null,
      risk: rec.risk ?? null,
      estimatedDurationSeconds: rec.estimated_duration_seconds ?? null,
      requiresConfirmation: Boolean(rec.requires_confirmation),
    })),
    narrative: api.narrative
      ? {
          summary: api.narrative.summary,
          rootCause: api.narrative.root_cause,
          impact: api.narrative.impact,
          evidence: api.narrative.evidence,
          recommendations: api.narrative.recommendations,
          confidence: api.narrative.confidence,
          fullText: api.narrative.full_text,
        }
      : null,
    relationships: api.relationships
      ? {
          parentIncidentId: api.relationships.parent_incident_id,
          childIncidentIds: api.relationships.child_incident_ids,
          duplicateIncidentIds: api.relationships.duplicate_incident_ids,
          recurring: api.relationships.recurring,
          recurrenceCount: api.relationships.recurrence_count,
          relatedDeploymentIds: api.relationships.related_deployment_ids,
          relatedMaintenanceWindows: api.relationships.related_maintenance_windows,
        }
      : null,
    correlatedIncidentIds: api.correlated_incident_ids ?? [],
  };
}

export function mapIncidentActionResult(api: import("@/lib/api").IncidentActionResultApi) {
  return {
    success: api.success,
    message: api.message,
    actionId: api.action_id,
    incidentId: api.incident_id,
    verification: api.verification,
    verificationPassed: api.verification_passed,
    policySummary: api.policy_summary,
    incidentResolved: api.incident_resolved,
    nextStep: api.next_step,
    diagnosticsAvailable: api.diagnostics_available,
    executionId: api.execution_id,
  };
}

export function mapDoctorRun(api: DoctorRunApi): DoctorRunResult {
  return {
    preview: api.preview,
    verdict: api.verdict,
    summary: {
      pass: api.summary.pass_count,
      warn: api.summary.warn_count,
      fail: api.summary.fail_count,
    },
    lastRuntimeDoctor: api.last_runtime_doctor,
    tenantsScanned: api.tenants_scanned,
  };
}
